import * as moment from "moment";
import { SettingsProperties } from "../../account/models/SettingsModels";
import { getLocationNames } from "../../api/locationsApi";

export interface CompanyModel {
    id: string;
    name: string;
    businessId: string;
    website?: string;
    address?: AddressModel,
    contact: ContactDetailsModel,
    contactPerson?: PersonModel,
    description?: string,
    industryType?: string,
    announcementCount: number,
    apiKey?: string,
    partnerApiKey?: string
}

export interface CompanyContactModel<TPhoneContactModel, TEmailContactModel> {
    phoneContacts: TPhoneContactModel[],
    emailContacts: TEmailContactModel[]
}

export type CompanyDataModel<TPhoneModel, TEmailModel> = CompanyModel & CompanyContactModel<TPhoneModel, TEmailModel>;

export type VisibleCompanyDataModel = CompanyDataModel<PhoneNumberModel, EmailModel>;

export interface RestrictedCompanyModel {
    id: string;
    restrictedCompanyId: string;
    businessId: string;
    name: string;
    city: string;
}

export interface PersonModelBase<TContact> {
    id: string;
    firstName: string;
    lastName: string;
    contact: TContact;
}

export type PersonModel = PersonModelBase<ContactDetailsModel>

export interface Employee extends PersonModel {
    company: string;
}

export interface AddressModel {
    streetAddress: string;
    postalCode: string;
    city: string;
    geoPoint?: GeoPoint;
}

export interface ContactModel {
    email?: string;
    phoneNumber?: string;
}

export interface ContactDetailsModel extends ContactModel {
    emailDetails?: string;
    phoneDetails?: string;
}

export interface ErrorModel {
    message: string;
}

export interface GeoPoint {
    latitude: number;
    longitude: number;
}

export interface LocationNameModel {
    id: string;
    name: string;
}

export interface LocationModel extends LocationNameModel {
    location: GeoPoint;
}

export enum FilterMode {
    All = "all",
    Company = "company",
    Own = "own"
}

export enum FilterTarget {
    Cargo = "Cargo", // 1, 
    Truck = "Truck", // 2, 
    Company = "Company" // 3
}

export interface FilterInfo {
    filterMode: FilterMode;
    fromDate: moment.Moment;
    toDate: moment.Moment;
    fromCities: string[];
    error?: string;
    toCities: string[];
    properties: any[];
    reporter: string[];
    reportNumberSearch: string;
}

export interface FilterInfoExtraData  { 
    filterMode: FilterMode;
    fromDate: moment.Moment;
    toDate: moment.Moment;
    fromCities: string[];
    error?: string;
    toCities: string[];
    properties: any[];
    reporter: string[];
    selectedReporters: Reporter[];
    reportNumberSearch: string;
    fromCityLocations: LocationNameModel[];
    toCityLocations: LocationNameModel[];
};

export interface MapFilterInfo {
    truckRoutes: boolean
    departingArrivingUnits: boolean,
    departingTrucks: boolean,
    companies: boolean
}

export interface UsedFilterSettingsModel {
    isInUse: boolean,
    model: FilterSettingsModel
}

export interface FilterSettingsModel {
    id: string;
    name: string;
    userId: string;
    tracking: boolean;
    targetType: FilterTarget;
    generalSearchField: string;
    validFrom: moment.Moment;
    validTo: moment.Moment;
    departureCityIds: string[];
    destinationCityIds: string[];
    reporters: string[];
    properties: SettingsProperties; 
    fromCityLocations: LocationNameModel[];
    toCityLocations: LocationNameModel[];
}

export const getEmptyFilterSettingsModel = (): FilterSettingsModel => {
    const startDate = moment();
    const tmpArr = String[0] = [];

    return {
        id: "",
        name: "",
        userId: "",
        tracking: false,
        targetType: FilterTarget.Cargo,
        generalSearchField: "",
        validFrom: startDate,
        validTo: startDate.clone().add(1, "hours"),
        departureCityIds: tmpArr,
        destinationCityIds: tmpArr,
        reporters: tmpArr,
        properties: {
            adr: false,
            explosive: false,
            thermal: false,
            tank: false,
            glass: false,
            mega: false,
            containerOver20: false,
            container20: false,
            pieceGoods: false,
            bulkGoods:false,
            trailer:false
        },
        fromCityLocations: [],
        toCityLocations: []
    }
}

export interface RouteModel {
    from: LocationModel;
    to: LocationModel;
    route: Array<[number, number]>;
    target: { id: string, departureCity: LocationModel, destinationCity: LocationModel, },
    distance: number

}
export enum DayOfWeek {
    Monday = "monday",
    Tuesday = "tuesday",
    Wednesday = "wednesday",
    Thursday = "thursday",
    Friday = "friday",
    Saturday = "saturday",
    Sunday = "sunday"
}

export interface RegisteredModel {
    registeredAt: moment.Moment;
}

export interface Reporter {
    id: string;
    name: string;
}

export interface DeletedReasonModel {
    id: string;
    name: string;
    contractCompanyId: string;
}

export interface IsOwn {
    isOwn: boolean;
}

export interface IsOwnCompany {
    isOwnCompany: boolean;
}

export interface DeleteReportProps<T> {
    selectedReason: T;
    candidate?: string;
}

export interface PhoneNumberModel {
    id?: string;
    phoneNumber: string;
    phoneDetails: string;
}

export interface EmailModel {
    id?: string;
    email: string;
    emailDetails: string;
}

export interface PhoneNumberSettingsModel extends PhoneNumberModel {
    phoneVisible: boolean;
}

export interface EmailSettingsModel extends EmailModel {
    emailVisible: boolean;
}

export interface EditMode {
    editMode?: "add" | "edit" | "copy"
}

export interface NotificationModel {
    id?: string;
    message: string;
    validFrom: Date | null;
    validTo: Date | null;
    redirectTo: string;
}

export enum CompanyIndustryType {
    Transportation = "transportation",
    Retail = "retail",
    Other = "other"
}

export const CompanyIndustryTypeOptions = (localizations: any) => {
    return (
    [
        { label: localizations.companyIndustryTypes[CompanyIndustryType.Transportation], value:  CompanyIndustryType.Transportation},
        { label: localizations.companyIndustryTypes[CompanyIndustryType.Retail], value: CompanyIndustryType.Retail },
        { label: localizations.companyIndustryTypes[CompanyIndustryType.Other], value: CompanyIndustryType.Other }
    ]);
} 


// WEBPACK FOOTER //
// ./src/common/models/CommonModels.ts